const GOOGLE_NEW_MEETING_URL =
  "https://calendar.google.com/calendar/u/0/r/eventedit?add=dashboard%40read.ai";

import { EnabledInValue } from "@app/common/types/serviceStatusTypes";

import type { Config as DevelopmentConfig } from "./config.development";
import type { Config as ProductionConfig } from "./config.production";
import type { Config as StagingConfig } from "./config.staging";
import type { Config as TestConfig } from "./config.test-env";

// The pattern is to put configuration that is applicable to all environments here
// and then override it in the environment-specific config files.
//
// DO NOT PUT SECRETS IN HERE!!!
const createCommonConfig = (
  config: TestConfig | DevelopmentConfig | StagingConfig | ProductionConfig
) => ({
  dates: {},
  flags: {
    dailyReadIsBeta: true,
    enableEuSettingsPopup: true,
    enableHomepage: false,
    enableHomepageTabs: false,
    enableInitialReadoutExperience: true,
    enableKeywordsOnSharedTopicReport: false,
    enableSalesforceIntegration: false,
    enableSpanishLocale: false,
    googleReview: false,
    // whether the Reports page will include topic report loading in its loading
    // state. set to false if/when topic report queries are slow
    waitForTopicReportsLoad: false,
    enableMobileNav: false,
  },
  releaseDates: {
    productivity: "03/25/2024",
    privacyPolicy: "03/26/2024",
    onboardingChecklist: "05/22/2024",
    initialReadoutExperience: "06/20/2024",
  } as Record<string, string>,
  calendar: {
    GOOGLE_URL: `${config.api.HOST}/calendar-connect/google`,
    MICROSOFT_URL: `${config.api.HOST}/calendar-connect/microsoft`,
    ZOOM_URL: `${config.api.HOST}/zoomapp/install?connect_calendar=True`,
    NEW_MEETING_URL: GOOGLE_NEW_MEETING_URL,
  },
  rss: {
    CHROME_WEB_STORE_URL:
      "https://chrome.google.com/webstore/detail/read-smart-scheduler-cale/aiamjjeggglngiggkmmbnpnpeejjejaf",
  },
  rssLinks: {
    maxCustom: 5,
  },
  zendesk: { key: "11845bde-7c74-4711-9e4f-60ced041f56b" },
  tracking: {
    DEBUG: false,
    ENABLED: true,
    MIXPANEL_HOST: "https://moxy.read.ai",
    MIXPANEL_VERBOSE_LOG: false,
    GOOGLE_CONVERSION_ID: null,
    LINKED_IN_PARTNER_ID: null,
  },
  billing: {
    trialDays: 7,
    maxSeatAdditions: 10000,
    zeroBalancePriceIds: [
      "price_1MBkf3KEmPi2JwflfiTtXwSX", // Read Free
      "price_1MFgs7KEmPi2JwflIgNhokWb", // Read Zoom
    ],
    credits: {
      price: 0.04, // price per single credit (=$20 for 500)
      packages: [500, 1000, 1500, 2000],
    },
    prices: {
      byPlan: {
        enterprise_plus: {
          monthly: 39.75,
          annual: 29.75,
        },
        enterprise: {
          monthly: 29.75,
          annual: 22.5,
        },
        pro: {
          monthly: 19.75,
          annual: 15,
        },
      },
      legacyByPlan: {
        enterprise_plus: {
          monthly: 39.75,
          annual: 29.75,
        },
        enterprise: {
          monthly: 25,
          annual: 16.5,
        },
        pro: {
          monthly: 15,
          annual: 10,
        },
      },
    },
    monthlyRecordingQuota: {
      enterprise_plus: null,
      enterprise: null,
      pro: null,
      free: 5,
      zoom_bundle_trial: 10,
      unknown: 5,
    },
    meetingRecordingMinutes: {
      free: 60,
      zoom_bundle_trial: 90,
      pro: 240,
      enterprise: 240,
      enterprise_plus: 480,
      unknown: 60,
    },
    monthlyUploadCredits: {
      free: 0,
      zoom_bundle_trial: 0,
      pro: 100,
      enterprise: 200, // careful when using this config! enterprise trials are not granted any credits
      enterprise_plus: 300,
      unknown: 0,
    },
    licenseMinimum: {
      free: 1,
      zoom_bundle_trial: 1,
      pro: 1,
      enterprise: 1,
      enterprise_plus: 10,
      unknown: 1,
    },
  },
  booking: {
    enableDevRoutes:
      import.meta.env.VITE_APP_ENABLE_BOOKING_DEV_ROUTES === "true",
  },
  auth: {
    google_one_tap: {
      AUTH_URL: `${config.api.HOST}/auth/google_one_tap`,
    },
    google: {
      AUTH_URL: `${config.api.HOST}/login/google`,
    },
    google_calendar: {
      AUTH_URL: `${config.api.HOST}/calendar-connect/google`,
      ENABLE_URL: `${config.api.HOST}/calendar-connection/google`,
    },
    zoom: {
      AUTH_URL: `${config.api.HOST}/login/zoom`,
    },
    zoom_calendar: {
      AUTH_URL: `${config.api.HOST}/zoomapp/install?connect_calendar=True`,
    },
    webex: {
      AUTH_URL: `${config.api.HOST}/login/webex`,
    },
    microsoft_graph: {
      AUTH_URL: `${config.api.HOST}/login/microsoft`,
    },
    microsoft_graph_calendar: {
      AUTH_URL: `${config.api.HOST}/calendar-connect/microsoft`,
      ENABLE_URL: `${config.api.HOST}/calendar-connection/microsoft`,
    },
    okta: {
      AUTH_URL: `${config.api.HOST}/login/okta`,
    },
    slack_sso: {
      AUTH_URL: `${config.api.HOST}/login/slack`,
    },
  },
  apps: {
    zoom: {
      DISPLAY_NAME: "Zoom",
      MARKETPLACE: "https://marketplace.zoom.us/apps/ViT60ZmbTpWGGWFt9CuUUw",
      INSTALL_URL: "https://api.read.ai/zoomapp/install",
    },
    webex: {
      DISPLAY_NAME: "Webex",
      MARKETPLACE:
        "https://apphub.webex.com/applications/analytics-by-read-dashboard-read-57622",
    },
    teams: {
      DISPLAY_NAME: "Teams",
      ADMIN_MAGIC_LINK: "/integrations/microsoft-teams-ingest/admin-magic-link",
      ADMIN_CONSENT_LINK: "/integrations/microsoft-teams-ingest/admin-consent",
      MARKETPLACE:
        "https://appsource.microsoft.com/en-us/product/office/WA200003896?tab=Overview",
    },
    meet: {
      ENABLE_MOCK_CLIENT: false,
      DISPLAY_NAME: "Meet",
      MARKETPLACE: "https://read.ai/meet",
      CLOUD_PROJECT_NUMBER: "unknown",
    },
  },
  integrations: {
    google_email: {
      AUTH_URL: `${config.api.HOST}/integrations/google-email/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/google-email/connection`,
    },
    hubspot: {
      AUTH_URL: `${config.api.HOST}/integrations/hubspot/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/hubspot/connection`,
    },
    microsoft_graph_email: {
      AUTH_URL: `${config.api.HOST}/integrations/microsoft-email/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/microsoft-email/connection`,
    },
    microsoft_graph_teams_chat_ingest: {
      AUTH_URL: `${config.api.HOST}/integrations/microsoft-teams-ingest/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/microsoft-teams-ingest/connection`,
    },
    microsoft_graph_teams_push: {
      AUTH_URL: `${config.api.HOST}/integrations/microsoft-teams-push/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/microsoft-teams-push/connection`,
    },
    salesforce: {
      AUTH_URL: `${config.api.HOST}/integrations/salesforce/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/salesforce/connection`,
    },
    slack_bot: {
      AUTH_URL: `${config.api.HOST}/integrations/slack/connect`,
      REMOVE_URL:
        "https://slack.com/apps/A04UUCUGESK-read-ai?tab=settings&next_id=0",
    },
    slack_ingest: {
      AUTH_URL: `${config.api.HOST}/integrations/slack-ingest/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/slack-ingest/connection`,
    },
    zapier: {
      CONFIGURE_URL: "https://zapier.com/app/zaps",
    },
  },
  sentry: {
    DSN: "https://6df7af02dacf407aba6f9c7a3353e8f9@o992397.ingest.sentry.io/5997695",
  },
  goLinks: {
    default: "/analytics/reports",
    newMeeting: GOOGLE_NEW_MEETING_URL,
    teamsApp: `https://teams.microsoft.com/l/app/${config.apps.teams.APP_ID}`,
    support: "https://support.read.ai/hc/en-us",
  },
  demoVideo: "https://player.vimeo.com/video/846755106?h=9cba6afad1",
  demoVideoOnSite: "https://www.read.ai/meeting-tools-demo-video",
  statusMessage: {
    enabled: [] as EnabledInValue[],
    text: "May 21 - Read is experiencing a processing delay of Meeting Reports. Reports may take longer than usual to display in your account.",
    severity: "info",
  },
  readWorkspaceId: "01G8HA5CS6MAR32DGQBS9EKPDE",
  policyVersions: {
    dataProcessingAgreement: "03/25/2024",
  },
  recaptchaSite: config.recaptchaSite,
});

export default createCommonConfig;
